import { Injectable, Inject } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError, finalize } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { Conversation } from '../models/assistant.model';
import { ASSISTANT_DATA_SERVICE } from '@pharvio/assistant/data-access';
import { AssistantDataService } from '../data/assistant-data.service';
import {
  LoadConversations,
  CreateConversation,
  SelectConversation,
  SendMessage,
  DeleteConversation,
  SetTyping
} from './assistant.actions';

export interface AssistantStateModel {
  conversations: Conversation[];
  activeConversationId: string | null;
  loading: boolean;
  typing: boolean;
}

@State<AssistantStateModel>({
  name: 'assistant',
  defaults: {
    conversations: [],
    activeConversationId: null,
    loading: false,
    typing: false
  }
})
@Injectable()
export class AssistantState {
  constructor(
    @Inject(ASSISTANT_DATA_SERVICE) private readonly svc: AssistantDataService
  ) {}

  @Selector() static conversations(s: AssistantStateModel): Conversation[] {
    return s.conversations;
  }
  @Selector() static activeConversationId(
    s: AssistantStateModel
  ): string | null {
    return s.activeConversationId;
  }
  @Selector() static loading(s: AssistantStateModel): boolean {
    return s.loading;
  }
  @Selector() static typing(s: AssistantStateModel): boolean {
    return s.typing;
  }
  @Selector() static activeConversation(
    s: AssistantStateModel
  ): Conversation | null {
    return s.conversations.find((c) => c.id === s.activeConversationId) ?? null;
  }

  @Action(LoadConversations)
  load(ctx: StateContext<AssistantStateModel>) {
    ctx.patchState({ loading: true });
    return this.svc.getConversations().pipe(
      tap((conversations) => ctx.patchState({ conversations, loading: false })),
      catchError(() => {
        ctx.patchState({ loading: false });
        return EMPTY;
      })
    );
  }

  @Action(CreateConversation)
  create(ctx: StateContext<AssistantStateModel>) {
    return this.svc.createConversation().pipe(
      tap((conv) =>
        ctx.patchState({
          conversations: [conv, ...ctx.getState().conversations],
          activeConversationId: conv.id
        })
      ),
      catchError(() => EMPTY)
    );
  }

  @Action(SelectConversation)
  select(ctx: StateContext<AssistantStateModel>, action: SelectConversation) {
    ctx.patchState({ activeConversationId: action.id });
  }

  @Action(SendMessage)
  send(ctx: StateContext<AssistantStateModel>, action: SendMessage) {
    const userMsg = {
      id: crypto.randomUUID(),
      role: 'user' as const,
      content: action.payload.content,
      timestamp: new Date().toISOString()
    };
    const withUser = ctx
      .getState()
      .conversations.map((c) =>
        c.id === action.payload.conversationId
          ? { ...c, messages: [...c.messages, userMsg] }
          : c
      );
    ctx.patchState({ conversations: withUser, typing: true });

    return this.svc.sendMessage(action.payload).pipe(
      tap((res) => {
        const withReply = ctx.getState().conversations.map((c) =>
          c.id === res.conversationId
            ? {
                ...c,
                title:
                  c.messages.filter((m) => m.role === 'user').length === 1
                    ? action.payload.content.slice(0, 40)
                    : c.title,
                messages: [...c.messages, res.message],
                updatedAt: new Date().toISOString()
              }
            : c
        );
        ctx.patchState({ conversations: withReply });
      }),
      finalize(() => ctx.patchState({ typing: false })),
      catchError(() => EMPTY)
    );
  }

  @Action(DeleteConversation)
  delete(ctx: StateContext<AssistantStateModel>, action: DeleteConversation) {
    return this.svc.deleteConversation(action.id).pipe(
      tap(() => {
        const convs = ctx
          .getState()
          .conversations.filter((c) => c.id !== action.id);
        const activeId =
          ctx.getState().activeConversationId === action.id
            ? convs[0]?.id ?? null
            : ctx.getState().activeConversationId;
        ctx.patchState({
          conversations: convs,
          activeConversationId: activeId
        });
      }),
      catchError(() => EMPTY)
    );
  }

  @Action(SetTyping)
  setTyping(ctx: StateContext<AssistantStateModel>, action: SetTyping) {
    ctx.patchState({ typing: action.typing });
  }
}
